import type {
  AdminLoginResponse,
  AdminSessionResponse,
  AdminVerifyResponse,
  FeedbackCreatedResponse,
  FeedbackPayload,
  RetentionExtensionResponse,
  RetentionPolicyResponse,
  WidgetRequestDetail,
  WidgetRequestItem,
} from '@/app/models/feedback.model';

export interface ApiConfig {
  apiUrl: string;
  tenantKey: string;
  authToken?: string;
  userId?: string;
  userEmail?: string;
  userRole?: 'user' | 'admin' | 'system';
}

export class ApiService {
  private userId: string;
  private userEmail: string;
  private userRole: 'user' | 'admin' | 'system';
  private adminSessionToken?: string;
  private analyticsSessionId?: string;
  private readonly adminSessionStorageKey: string;
  private readonly widgetIdentityStorageKey: string;
  private readonly defaultUserId: string;
  private readonly defaultUserEmail: string;
  private readonly defaultUserRole: 'user' | 'admin' | 'system';

  constructor(private readonly config: ApiConfig) {
    this.adminSessionStorageKey = `faas_admin_session:${this.config.apiUrl}:${this.config.tenantKey}`;
    this.widgetIdentityStorageKey = `faas_widget_identity:${this.config.apiUrl}:${this.config.tenantKey}`;
    const fallbackIdentity = this.resolveFallbackWidgetIdentity();
    const configUserId = this.normalizeIdentityValue(this.config.userId);
    const configUserEmail = this.normalizeIdentityValue(this.config.userEmail);
    this.userId = configUserId || fallbackIdentity.userId;
    this.userEmail =
      configUserEmail ||
      (configUserId ? this.buildSyntheticEmail(configUserId) : fallbackIdentity.userEmail);
    this.userRole = this.config.userRole || 'user';
    this.defaultUserId = this.userId;
    this.defaultUserEmail = this.userEmail;
    this.defaultUserRole = this.userRole;
    this.adminSessionToken = this.readStoredAdminSessionToken();
  }

  private normalizeIdentityValue(value?: string): string | undefined {
    if (!value) return undefined;
    const normalized = value.trim();
    return normalized || undefined;
  }

  private buildSyntheticEmail(userId: string): string {
    const slug =
      userId
        .toLowerCase()
        .replace(/[^a-z0-9._-]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'widget-user';
    return `${slug}@widget.local`;
  }

  private resolveFallbackWidgetIdentity(): { userId: string; userEmail: string } {
    const stored = this.readStoredWidgetIdentity();
    if (stored) return stored;

    const generatedId = this.generateWidgetIdentityId();
    const identity = {
      userId: generatedId,
      userEmail: `${generatedId}@widget.local`,
    };
    this.writeStoredWidgetIdentity(identity);
    return identity;
  }

  private generateWidgetIdentityId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `widget-${crypto.randomUUID()}`;
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      const random = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      return `widget-${Date.now().toString(36)}-${random}`;
    }
    return `widget-${Date.now().toString(36)}`;
  }

  private readStoredWidgetIdentity(): { userId: string; userEmail: string } | undefined {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return undefined;
      const raw = window.localStorage.getItem(this.widgetIdentityStorageKey);
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as { userId?: unknown; userEmail?: unknown };
      if (typeof parsed.userId !== 'string' || typeof parsed.userEmail !== 'string')
        return undefined;
      if (!parsed.userId.trim() || !parsed.userEmail.trim()) return undefined;
      return { userId: parsed.userId, userEmail: parsed.userEmail };
    } catch {
      return undefined;
    }
  }

  private writeStoredWidgetIdentity(identity: { userId: string; userEmail: string }): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;
      window.localStorage.setItem(this.widgetIdentityStorageKey, JSON.stringify(identity));
    } catch {
      // Ignore storage errors.
    }
  }

  hasStoredAdminSession(): boolean {
    return Boolean(this.adminSessionToken);
  }

  setUserContext(context: {
    userId?: string;
    userEmail?: string;
    userRole?: 'user' | 'admin' | 'system';
  }): void {
    const nextUserId = this.normalizeIdentityValue(context.userId);
    const nextUserEmail = this.normalizeIdentityValue(context.userEmail);

    if (nextUserId) {
      this.userId = nextUserId;
    }
    if (nextUserEmail) {
      this.userEmail = nextUserEmail;
    } else if (nextUserId) {
      // Keep email optional for host apps while still sending a stable identity header.
      this.userEmail = this.buildSyntheticEmail(nextUserId);
    }
    if (context.userRole) {
      this.userRole = context.userRole;
    } else if (nextUserId || nextUserEmail) {
      this.userRole = 'user';
    }
  }

  async getCategories(): Promise<Array<{ id: string; name: string }>> {
    const response = await fetch(this.url('/categories'), {
      method: 'GET',
      headers: this.headers(),
    });
    if (!response.ok) {
      throw new Error('Unable to load categories');
    }
    const data = (await response.json()) as { items: Array<{ id: string; name: string }> };
    return data.items;
  }

  // ─── Usage analytics ───────────────────────────────────────────────
  // Lightweight, fire-and-forget tracking that feeds the existing admin
  // analytics dashboard (/analytics/track/* endpoints). Failures are
  // swallowed so instrumentation can never break the widget experience.

  private detectDeviceType(ua: string): string {
    if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
    if (/iPad|Tablet/i.test(ua)) return 'tablet';
    return 'desktop';
  }

  private detectBrowser(ua: string): string {
    if (/Edg\//i.test(ua)) return 'Edge';
    if (/OPR\//i.test(ua) || /Opera/i.test(ua)) return 'Opera';
    if (/Chrome\//i.test(ua)) return 'Chrome';
    if (/Firefox\//i.test(ua)) return 'Firefox';
    if (/Safari\//i.test(ua)) return 'Safari';
    return 'Other';
  }

  /** Start (once) an analytics session for the current widget user. */
  async initAnalyticsSession(): Promise<void> {
    if (this.analyticsSessionId) return;
    try {
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      const response = await fetch(this.url('/analytics/track/session'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: this.config.tenantKey,
          user_id: this.userId,
          user_email: this.userEmail,
          device_type: this.detectDeviceType(ua),
          browser: this.detectBrowser(ua),
          user_agent: ua || null,
        }),
      });
      if (!response.ok) return;
      const data = (await response.json()) as { session_id?: string };
      if (data.session_id) this.analyticsSessionId = data.session_id;
    } catch {
      // Analytics must never break the widget.
    }
  }

  /** Record a widget usage event (feeds the admin "features" analytics). */
  async trackEvent(
    featureName: string,
    action: string,
    metadata?: Record<string, unknown>
  ): Promise<void> {
    try {
      await fetch(this.url('/analytics/track/event'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: this.config.tenantKey,
          session_id: this.analyticsSessionId ?? null,
          user_id: this.userId,
          event_type: 'widget',
          feature_name: featureName,
          action,
          metadata: metadata ?? null,
        }),
      });
    } catch {
      // Ignore analytics failures.
    }
  }

  async submitFeedback(payload: FeedbackPayload): Promise<FeedbackCreatedResponse> {
    if (!payload.screenshot) {
      throw new Error('Screenshot is required before submitting feedback.');
    }

    const body = new FormData();
    body.append('text', payload.text);
    if (payload.categoryId) body.append('category_id', payload.categoryId);
    if (typeof payload.rating === 'number' && Number.isFinite(payload.rating)) {
      body.append('rating', String(payload.rating));
    }
    if (payload.metadata) body.append('metadata', JSON.stringify(payload.metadata));
    if (payload.sourceUrl) body.append('source_url', payload.sourceUrl);
    if (payload.pageTitle) body.append('page_title', payload.pageTitle);
    if (payload.viewport) {
      body.append('viewport_width', String(payload.viewport.width));
      body.append('viewport_height', String(payload.viewport.height));
    }
    body.append('screenshot', payload.screenshot, 'feedback.png');

    const response = await fetch(this.url('/feedback'), {
      method: 'POST',
      // Public feedback submission must work without login/session.
      headers: this.headers(false, undefined, false, false),
      body,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Unable to submit feedback');
    }
    return (await response.json()) as FeedbackCreatedResponse;
  }

  async createWidgetRequest(payload: {
    screenshot?: Blob;
    title?: string;
    description?: string;
    metadata?: Record<string, unknown>;
    sourceUrl?: string;
    pageTitle?: string;
    viewport?: { width: number; height: number };
  }): Promise<{ id: string; status: string; created_at?: string }> {
    const body = new FormData();
    if (payload.screenshot) body.append('screenshot', payload.screenshot, 'request.png');
    if (payload.title) body.append('title', payload.title);
    if (payload.description) body.append('description', payload.description);
    if (payload.metadata) body.append('metadata', JSON.stringify(payload.metadata));
    if (payload.sourceUrl) body.append('source_url', payload.sourceUrl);
    if (payload.pageTitle) body.append('page_title', payload.pageTitle);
    if (payload.viewport) {
      body.append('viewport_width', String(payload.viewport.width));
      body.append('viewport_height', String(payload.viewport.height));
    }

    const response = await fetch(this.url('/widget/requests'), {
      method: 'POST',
      // Public widget request submission must work without login/session.
      headers: this.headers(false, undefined, false, false),
      body,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Unable to create request');
    }
    return (await response.json()) as { id: string; status: string; created_at?: string };
  }

  async listWidgetRequests(): Promise<WidgetRequestItem[]> {
    const allItems: WidgetRequestItem[] = [];
    let page = 1;
    const perPage = 100;
    while (true) {
      const response = await fetch(this.url(`/widget/requests?page=${page}&per_page=${perPage}`), {
        method: 'GET',
        // Send the widget admin session token when logged in so the integration
        // admin sees every request in their integration; plain widget users
        // (no session token) still see only their own submissions.
        headers: this.headers(true, undefined, true, false),
      });
      if (!response.ok) {
        throw new Error('Unable to load requests');
      }
      const data = (await response.json()) as { items: WidgetRequestItem[]; total: number };
      const items = data.items || [];
      allItems.push(...items);
      if (allItems.length >= data.total || items.length < perPage) {
        break;
      }
      page++;
    }
    return allItems;
  }

  async getWidgetRequest(id: string): Promise<WidgetRequestDetail> {
    const response = await fetch(this.url(`/widget/requests/${id}`), {
      method: 'GET',
      // Send the widget admin session token when logged in so the integration
      // admin can open any request in their integration.
      headers: this.headers(true, undefined, true, false),
    });
    if (!response.ok) {
      throw new Error('Unable to load request detail');
    }
    return (await response.json()) as WidgetRequestDetail;
  }

  async deleteWidgetRequest(id: string): Promise<void> {
    const response = await fetch(this.url(`/widget/requests/${id}`), {
      method: 'DELETE',
      headers: this.headers(),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Unable to delete request');
    }
  }

  async adminLogin(admin: { email: string; password: string }): Promise<AdminLoginResponse> {
    const response = await fetch(this.url('/widget/admin/login'), {
      method: 'POST',
      headers: this.headers(true, undefined, false, false),
      body: JSON.stringify({ email: admin.email, password: admin.password }),
    });
    if (!response.ok) {
      const message = await this.readErrorMessage(response, 'Admin login failed');
      if (message.toLowerCase().includes('generated integration admin credentials')) {
        throw new Error(
          'Use integration admin email/password from the Admin Integration list (not admin@example.com).'
        );
      }
      throw new Error(message);
    }
    const result = (await response.json()) as AdminLoginResponse;
    this.adminSessionToken = result.session_token;
    this.writeStoredAdminSessionToken(result.session_token);
    this.userRole = 'admin';
    this.userEmail = admin.email;
    return result;
  }

  /** Effective image-retention policy for the logged-in widget admin's integration. */
  async getRetentionPolicy(): Promise<RetentionPolicyResponse | null> {
    const response = await fetch(this.url('/retention-extensions/widget/policy'), {
      method: 'GET',
      headers: this.headers(true, undefined, true, false),
    });
    if (!response.ok) return null;
    return (await response.json()) as RetentionPolicyResponse;
  }

  /** Raise a retention-extension request for the widget admin's integration (super admin approves). */
  async requestRetentionExtension(payload: {
    reason: string;
    requested_days: number;
    integration_id?: string;
    integration_name?: string;
  }): Promise<RetentionExtensionResponse> {
    const response = await fetch(this.url('/retention-extensions/widget'), {
      method: 'POST',
      headers: this.headers(true, undefined, true, false),
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(
        await this.readErrorMessage(response, 'Could not submit the extension request')
      );
    }
    return (await response.json()) as RetentionExtensionResponse;
  }

  async getAdminSession(): Promise<AdminSessionResponse> {
    const response = await fetch(this.url('/widget/admin/session'), {
      method: 'GET',
      headers: this.headers(true, undefined, true, false),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Admin session invalid');
    }
    const session = (await response.json()) as AdminSessionResponse;
    this.userRole = 'admin';
    if (session.email) this.userEmail = session.email;
    return session;
  }

  async adminVerify(): Promise<AdminVerifyResponse> {
    const response = await fetch(this.url('/widget/admin/verify'), {
      method: 'POST',
      headers: this.headers(true, undefined, true, false),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Admin verify failed');
    }
    return (await response.json()) as AdminVerifyResponse;
  }

  async approveRequest(id: string, adminActionToken: string): Promise<void> {
    const response = await fetch(this.url(`/widget/admin/requests/${id}/approve`), {
      method: 'PATCH',
      headers: this.headers(true, adminActionToken),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Approve failed');
    }
  }

  async rejectRequest(id: string, adminActionToken: string, comment?: string): Promise<void> {
    const response = await fetch(this.url(`/widget/admin/requests/${id}/reject`), {
      method: 'PATCH',
      headers: this.headers(true, adminActionToken),
      body: JSON.stringify({ comment: comment || '' }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Reject failed');
    }
  }

  async commentRequest(id: string, adminActionToken: string, comment: string): Promise<void> {
    const response = await fetch(this.url(`/widget/admin/requests/${id}/comments`), {
      method: 'POST',
      headers: this.headers(true, adminActionToken),
      body: JSON.stringify({ comment }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Comment failed');
    }
  }

  async updateRequestStatus(
    id: string,
    status: 'submitted' | 'in_review' | 'needs_info' | 'approved' | 'rejected',
    adminActionToken: string,
    comment?: string
  ): Promise<void> {
    const response = await fetch(this.url(`/widget/admin/requests/${id}/status`), {
      method: 'PATCH',
      headers: this.headers(true, adminActionToken),
      body: JSON.stringify({ status, comment: comment || null }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Status update failed');
    }
  }

  clearAdminSession(): void {
    this.adminSessionToken = undefined;
    this.writeStoredAdminSessionToken(undefined);
    this.userId = this.defaultUserId;
    this.userEmail = this.defaultUserEmail;
    this.userRole = this.defaultUserRole;
  }

  private readStoredAdminSessionToken(): string | undefined {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) return undefined;
      return window.sessionStorage.getItem(this.adminSessionStorageKey) || undefined;
    } catch {
      return undefined;
    }
  }

  private writeStoredAdminSessionToken(token?: string): void {
    try {
      if (typeof window === 'undefined' || !window.sessionStorage) return;
      if (token) {
        window.sessionStorage.setItem(this.adminSessionStorageKey, token);
      } else {
        window.sessionStorage.removeItem(this.adminSessionStorageKey);
      }
    } catch {
      // Ignore storage errors (private mode/quota/security settings).
    }
  }

  private headers(
    includeJson = true,
    adminActionToken?: string,
    includeSessionToken = true,
    includeConfigAuthToken = true
  ): HeadersInit {
    const headers: Record<string, string> = {
      'X-Tenant-Key': this.config.tenantKey,
      'X-User-Id': this.userId,
      'X-User-Email': this.userEmail,
      'X-User-Role': this.userRole,
    };
    if (includeSessionToken && this.adminSessionToken) {
      headers.Authorization = `Bearer ${this.adminSessionToken}`;
    }
    if (adminActionToken) headers['X-Admin-Action-Token'] = adminActionToken;
    if (includeConfigAuthToken && this.config.authToken && !headers.Authorization) {
      headers.Authorization = `Bearer ${this.config.authToken}`;
    }
    if (includeJson) {
      headers['Content-Type'] = 'application/json';
    }
    return headers;
  }

  private url(path: string): string {
    const base = this.config.apiUrl.replace(/\/$/, '');
    if (base.endsWith('/feedback')) {
      return `${base.slice(0, -'/feedback'.length)}${path}`;
    }
    return `${base}${path}`;
  }

  private async readErrorMessage(response: Response, fallback: string): Promise<string> {
    let text = '';
    try {
      text = await response.text();
    } catch {
      return fallback;
    }
    if (!text.trim()) return fallback;
    try {
      const parsed = JSON.parse(text) as { detail?: unknown; message?: unknown };
      if (typeof parsed.detail === 'string' && parsed.detail.trim()) return parsed.detail;
      if (typeof parsed.message === 'string' && parsed.message.trim()) return parsed.message;
    } catch {
      // Response is plain text.
    }
    return text;
  }
}
